import { DisplayValue, isNullOrUndefined } from '@adaptiva/core/common';
import { LoggingService } from '@adaptiva/core/logging';
import { UiService } from '@adaptiva/core/ui';
import { FormConfiguration, IFormConfiguration, IFormDataOutput } from '@adaptiva/forms/common';
import { FormRendererComponent } from '@adaptiva/forms/widgets';
import { EInstallerType, ELoggedOnUser, MetadataAuthoringProperties } from '@adaptiva/metadata/common';
import { ExpressionLanguageEditorComponent } from '@adaptiva/widgets/expression-editor';
import { OverlayComponent } from '@adaptiva/widgets/overlay';
import { ITimeScaleConfiguration, TimeScaleConfiguration, TimeScaleUnit } from '@adaptiva/widgets/time-scale';
import { AfterViewInit, ChangeDetectorRef, Directive, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BaseAuthoringPropertiesComponent } from '../base-authoring-component';

@Directive()
export abstract class BaseFormAuthoringComponent<T extends MetadataAuthoringProperties> extends BaseAuthoringPropertiesComponent<T> implements AfterViewInit {

	@ViewChild('AutoItScriptEditor')
	public autoItScriptEditor: ExpressionLanguageEditorComponent;

	@ViewChild('ConfigOverlay')
	public configOverlay: OverlayComponent;

	@ViewChild('PrereqSensorOverlay')
	private _prereqSensorOverlay: OverlayComponent;

	@ViewChildren(FormRendererComponent)
	private _formRendererListener: QueryList<FormRendererComponent>;

	public installerTypeOptions: DisplayValue[] = [
		new DisplayValue('MSI', EInstallerType.MSI),
		new DisplayValue('MSIX', EInstallerType.MSIX),
		new DisplayValue('MSIX Bundle', EInstallerType.MSIX_BUNDLE),
		new DisplayValue('Silent EXE', EInstallerType.SILENT_EXE),
		new DisplayValue('Non Silent EXE', EInstallerType.NON_SILENT_EXE),
		new DisplayValue('Powershell Script', EInstallerType.POWERSHELL_SCRIPT),
		new DisplayValue('VB Script', EInstallerType.VB_SCRIPT),
		new DisplayValue('Batch File', EInstallerType.BATCH_FILE),
	];

	public requiredOptions: DisplayValue[] = [
		new DisplayValue('Required', ELoggedOnUser.REQUIRED),
		new DisplayValue('Prohibited', ELoggedOnUser.PROHIBITED),
		new DisplayValue('Don\'t Care', ELoggedOnUser.DONT_CARE),
	];

	public prerequisiteExpressions$: BehaviorSubject<string[]> = new BehaviorSubject([]);

	public expressionEditingIndex: int = -1;

	public selectedPrereqExpression: string = '';

	public maxRuntimeScaleConfig: ITimeScaleConfiguration = {
		format: TimeScaleConfiguration.SCALE_MILLISECONDS,
		scaleUnits: [
			new TimeScaleUnit('hours'),
			new TimeScaleUnit('minutes'),
			new TimeScaleUnit('seconds')
		]
	};

	public formConfiguration: IFormConfiguration;

	constructor(
		public UI: UiService,
		protected logger: LoggingService,
		protected cd: ChangeDetectorRef
	) {
		super(UI);
	}

	public commitModelToView(): void {
		const _uiConfiguration: IFormConfiguration = this.currentPropertyObject['CustomizerUI'] || this.inheritedPropertyObject?.['CustomizerUI'];
		const _expressions: string[] = this.currentPropertyObject['PrerequisiteSensorExpressions'] || [];

		this.formConfiguration = _uiConfiguration ? cloneDeep(_uiConfiguration) : new FormConfiguration();
		this.prerequisiteExpressions$.next(_expressions);
	}

	public inheritPrerequisiteSensorExpressions(): void {
		this.deleteObjectPropertyValue('PrerequisiteSensorExpressions');

		if (this.inheritedPropertyObject?.hasOwnProperty('PrerequisiteSensorExpressions'))
			this.prerequisiteExpressions$.next(this.inheritedPropertyObject['PrerequisiteSensorExpressions']);
		else
			this.prerequisiteExpressions$.next([]);
	}

	public inheritAutoItScript(): void {
		this.deleteObjectPropertyValue('AutoItScript');
		this.autoItScriptEditor.setValue((this.inheritedPropertyObject as any)?.AutoItScript || '');
	}

	public autoItScriptChange(_event: string): void {
		if (_event)
			this.setObjectPropertyValue('AutoItScript', _event);
	}

	public getFormKeyProperties(_config: IFormConfiguration): DisplayValue[] {
		return _config.layout.body.rows.reduce<DisplayValue[]>((_keyProperties, _row) => {
			const _columnProps: DisplayValue[] = _row.columns.map(_col => {
				return new DisplayValue(_col.content.propertyBindings.inputKey, _col.content.propertyBindings.defaultValue);
			});

			return _keyProperties.concat(_columnProps);
		}, []);
	}

	public onConfigurationSubmit(_submit: boolean): void {
		if (!_submit)
			return;

		this._updateCustomizerForm(this.formConfiguration);
	}

	private _updateCustomizerForm(_config: IFormConfiguration): void {
		// Construct object w/ current properties
		const _cpo: T = this.currentPropertyObject;
		const _copy: T = new this.propertyConstructor();

		for (const _property in _cpo) {
			// copy all properties except "custom" properties derived from form config
			if (!isNullOrUndefined(_cpo[_property]) && !_property.startsWith('Custom_'))
				_copy[_property] = _cpo[_property];
		}

		// overwrite CustomizerUI from form config
		_copy['CustomizerUI'] = _config;

		// Append form properties to object from scratch each time
		this.getFormKeyProperties(_config).forEach((_prop: DisplayValue) => {
			if (!_prop.display.startsWith('Custom_'))
				_copy[`Custom_${_prop.display}`] = _prop.value;
		});
		this.setCurrentPropertyObject(_copy);
	}

	public openPrereqOverlay(_index?: int): void {
		if (_index > -1) {
			this.expressionEditingIndex = _index;
			this.selectedPrereqExpression = this.prerequisiteExpressions$.getValue()[_index];
		} else {
			this.expressionEditingIndex = -1;
			this.selectedPrereqExpression = '';
		}

		this.cd.detectChanges();
		this._prereqSensorOverlay?.open();
	}

	public removePrereqExpression(_index: int): void {
		const _expressions: string[] = this.prerequisiteExpressions$.getValue();

		_expressions.splice(_index, 1);
		this.cd.detectChanges();
		this.setObjectPropertyValue('PrerequisiteSensorExpressions', _expressions);
	}

	public addPrereqExpression(): void {
		const _expressions: string[] = this.prerequisiteExpressions$.getValue();

		if (this.expressionEditingIndex > -1)
			_expressions[this.expressionEditingIndex] = this.selectedPrereqExpression;
		else {
			_expressions.push(this.selectedPrereqExpression);
			this.cd.detectChanges();
		}

		this.setObjectPropertyValue('PrerequisiteSensorExpressions', _expressions);
	}

	public override ngAfterViewInit(): void {
		super.ngAfterViewInit();

		if (this.forCustomization) {
			this._formRendererListener.changes.pipe(
				this.takeUntilDestroyed(),
				switchMap((_list: QueryList<FormRendererComponent>) => {
					if (isNullOrUndefined(_list?.first))
						return of(null);

					return _list.first.getFormOutput();
				}),
				this.takeUntilDestroyed(),
			).subscribe({
				next: (_output: IFormDataOutput) => this._handleRendererChanges(_output)
			});

			if (this._formRendererListener.first)
				this._formRendererListener.notifyOnChanges();
		}
	}

	private _handleRendererChanges(_output: IFormDataOutput): void {
		if (isNullOrUndefined(_output))
			return;

		const _outputEntries: [string, any][] = Object.entries(_output.data);

		_outputEntries.forEach(([_key, _value]: [string, any], _index: number) => {
			this.setObjectPropertyValue(`Custom_${_key}`, _value, _index === _outputEntries.length - 1);
		});
	}

	public deleteCustomizerUi(): void {
		if (!isNullOrUndefined(this.currentPropertyObject)) {
			Object.keys(this.currentPropertyObject)
				.filter((_propertyKey: string) => _propertyKey.startsWith('Custom_'))
				.forEach((_customKey: string) => delete this.currentPropertyObject[_customKey]);
		}

		this.deleteObjectPropertyValue('CustomizerUI');
		this.formConfiguration = new FormConfiguration();
	}

}
